import { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { datetimePickerHandlers } from '@/lib/datetime-picker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postCount: number;
  isPending: boolean;
  /** Caller submits the chosen ISO datetime (or undefined if invalid).
   *  Dialog stays open while pending; caller closes via onOpenChange. */
  onConfirm: (scheduledAt: string) => void;
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function startOfNextDayAt(hour: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function nextMondayAt(hour: number): Date {
  const d = new Date();
  const dow = d.getDay();
  const offset = dow === 1 ? 7 : (1 - dow + 7) % 7 || 7;
  d.setDate(d.getDate() + offset);
  d.setHours(hour, 0, 0, 0);
  return d;
}

const PRESETS: { label: string; compute: () => Date }[] = [
  { label: '+1 ชม.', compute: () => new Date(Date.now() + 60 * 60 * 1000) },
  { label: 'พรุ่งนี้ 9:00', compute: () => startOfNextDayAt(9) },
  { label: 'พรุ่งนี้ 18:00', compute: () => startOfNextDayAt(18) },
  { label: 'จันทร์ 9:00', compute: () => nextMondayAt(9) },
];

/**
 * Schedule every selected post at the SAME future timestamp. Per-post
 * times would need a different UX (per-row picker); same-time covers
 * the realistic editorial-calendar use case of "drop this batch of
 * features at 9am Monday".
 *
 * Re-uses the preset shapes from PublishDialog so muscle memory
 * carries between single-post and bulk flows.
 */
export function BulkScheduleDialog({ open, onOpenChange, postCount, isPending, onConfirm }: Props) {
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Default to 1 hour from now so the date input isn't blank.
      setScheduledAt(toDatetimeLocalValue(new Date(Date.now() + 60 * 60 * 1000)));
      setError(null);
    }
  }, [open]);

  const handleConfirm = () => {
    if (!scheduledAt) {
      setError('กรุณาเลือกวันเวลา');
      return;
    }
    const date = new Date(scheduledAt);
    if (Number.isNaN(date.getTime())) {
      setError('รูปแบบวันเวลาไม่ถูกต้อง');
      return;
    }
    if (date.getTime() <= Date.now()) {
      setError('เวลาต้องเป็นอนาคต');
      return;
    }
    onConfirm(date.toISOString());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-slate-400" />
            ตั้งเวลาเผยแพร่ {postCount} บทความพร้อมกัน
          </DialogTitle>
          <DialogDescription>
            ทุกบทความที่เลือกจะถูกเผยแพร่ตามเวลาเดียวกัน — cron 5 นาทีจะ flip status เมื่อถึงเวลา
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => {
                  setScheduledAt(toDatetimeLocalValue(p.compute()));
                  setError(null);
                }}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {p.label}
              </button>
            ))}
          </div>
          <Label htmlFor="bulk-scheduledAt" className="text-xs">
            วันเวลาเผยแพร่
          </Label>
          <Input
            id="bulk-scheduledAt"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => {
              setScheduledAt(e.target.value);
              setError(null);
            }}
            {...datetimePickerHandlers}
            className="cursor-pointer"
          />
          {error && <p className="text-destructive text-xs">{error}</p>}
          <p className="text-[11px] text-slate-500">
            เวลาตามเครื่องคุณ — ระบบจะเก็บเป็น UTC อัตโนมัติ. รายการที่ไม่มีสิทธิ์ publish จะถูกข้าม
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            ยกเลิก
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isPending}>
            {isPending ? 'กำลังตั้งเวลา...' : 'ตั้งเวลา'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
